import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, FileText, CalendarClock, CheckCircle2, Send, Loader2, MessageSquare, Download, Upload, X, Image as ImageIcon, Pencil, Mic, Video, ExternalLink } from 'lucide-react'
import { assignmentApi, type Assignment, type Submission } from '../api/lessonBuilderApi'
import { dueStatus, fmtDue, DUE_TONE_CLASS } from '../types/CourseDoc'
import KaTeXRenderer from '../../../components/common/KaTeXRenderer'
import PdfPreview from '../components/PdfPreview'
import AnnotatedImage from '../components/AnnotatedImage'
import { openExternalClick, downloadFile } from '../lib/openExternal'

const isPdf = (u?: string) => !!u && /\.pdf($|\?)/i.test(u)
const isDirectVideo = (u?: string) => !!u && /\.(mp4|webm|ogg|mov|m4v)($|\?)/i.test(u)

// Trang học viên: xem đề (ảnh/PDF/KaTeX), nộp file lời giải (ảnh/PDF), xem điểm & nhận xét GV.
const AssignmentDoPage: React.FC = () => {
  const { id } = useParams()
  const [a, setA] = useState<Assignment | null | undefined>(undefined)
  const [mine, setMine] = useState<Submission | null>(null)
  const [content, setContent] = useState('')
  const [files, setFiles] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [msg, setMsg] = useState('')

  useEffect(() => {
    document.title = 'Bài tập | VatLi365'
    if (!id) { setA(null); return }
    assignmentApi.get(id).then((r) => {
      if (!r) { setA(null); return }
      setA(r.assignment)
      setMine(r.mySubmission)
      if (r.mySubmission) {
        setContent(r.mySubmission.content || '')
        setFiles(r.mySubmission.files && r.mySubmission.files.length ? r.mySubmission.files : (r.mySubmission.file_url ? [r.mySubmission.file_url] : []))
      }
    }).catch(() => setA(null))
  }, [id])

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]; e.target.value = ''
    if (!f || !id) return
    setUploading(true); setMsg('')
    try {
      const r = await assignmentApi.uploadFile(id, f)
      setFiles((prev) => [...prev, r.url])
    } catch { setMsg('Tải file lên chưa thành công (chỉ nhận ảnh/PDF ≤ 25MB).') } finally { setUploading(false) }
  }

  const submit = async () => {
    if (!id) return
    if (!content.trim() && files.length === 0) { setMsg('Hãy nhập bài làm hoặc tải lên file lời giải.'); return }
    setBusy(true); setMsg('')
    try {
      const s = await assignmentApi.submit(id, content, files)
      setMine(s)
      setMsg('✅ Đã nộp bài. Bạn có thể nộp lại đến khi giáo viên chấm.')
    } catch (e) {
      const m = (e as Error)?.message || ''
      setMsg(m.includes('400') ? 'Bài đã được chấm — không thể nộp lại.' : 'Nộp bài chưa thành công, thử lại.')
    } finally { setBusy(false) }
  }

  if (a === undefined) return <div className="min-h-screen flex items-center justify-center text-gray-400">Đang tải…</div>
  if (!a) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#F8FAFC] text-center px-4">
      <FileText className="w-12 h-12 text-gray-300 mb-3" />
      <p className="text-gray-500 mb-4">Không tìm thấy bài tập.</p>
      <Link to="/khoa-hoc" className="text-blue-600 font-semibold">← Khoá học</Link>
    </div>
  )

  const graded = mine?.status === 'graded'
  const ds = a.due_date ? dueStatus(a.due_date, { completed: !!mine }) : null
  const rubric = a.rubric || []
  const annos = mine?.annotations || []
  const feedbackAudioUrl = mine?.feedback_audio_url || ''
  const feedbackVideoUrl = mine?.feedback_video_url || ''
  // Gom breakdown theo Câu (giữ thứ tự); mục không có cau → nhóm phẳng.
  const groups: { cau: string; idxs: number[] }[] = []
  rubric.forEach((r, i) => {
    const key = r.cau || ''
    const last = groups[groups.length - 1]
    if (last && last.cau === key) last.idxs.push(i)
    else groups.push({ cau: key, idxs: [i] })
  })

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Link to="/dang-hoc" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800 mb-4">
          <ArrowLeft size={16} /> Đang học
        </Link>

        {/* Đề bài */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-4">
          <div className="flex items-center gap-2 text-xs font-bold text-blue-600 uppercase tracking-wide mb-2">
            <FileText size={15} /> Bài tập tự luận · Thang {a.max_score}đ
          </div>
          <h1 className="text-xl font-black text-slate-900 mb-2">{a.title}</h1>

          {/* Đề dạng ảnh / PDF (giữ nguyên) hoặc văn bản KaTeX */}
          {a.attachment_url && a.attachment_type === 'image' && (
            <img src={a.attachment_url} alt="Đề bài" className="w-full rounded-xl border border-gray-200 my-2" />
          )}
          {a.attachment_url && a.attachment_type === 'pdf' && (
            <PdfPreview url={a.attachment_url} maxPages={20} className="my-2 max-h-[75vh] overflow-auto rounded-xl border border-gray-200 bg-white p-1" />
          )}
          {a.prompt && <div className="text-slate-700 leading-relaxed my-1"><KaTeXRenderer content={a.prompt} /></div>}

          {a.attachment_url && (
            <a href={a.attachment_url} onClick={openExternalClick(a.attachment_url)}
              className="inline-flex items-center gap-1.5 mt-2 px-3 py-1.5 rounded-lg bg-slate-100 text-slate-700 text-sm font-bold hover:bg-slate-200">
              <Download size={15} /> Tải đề ({a.attachment_type === 'pdf' ? 'PDF' : 'ảnh'})
            </a>
          )}

          {ds && (
            <div className={`mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-lg border text-sm font-semibold ${DUE_TONE_CLASS[ds.tone]}`}>
              <CalendarClock size={15} /> Hạn nộp {fmtDue(a.due_date)} · {ds.label}
            </div>
          )}
        </div>

        {/* Kết quả đã chấm */}
        {graded && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 mb-4">
            <div className="flex items-center gap-2 text-emerald-800 font-black mb-1">
              <CheckCircle2 size={18} /> Điểm: {mine?.score}/{a.max_score}
            </div>
            {rubric.length > 0 && mine?.itemScores && mine.itemScores.length > 0 && (
              <div className="text-sm text-emerald-900 mt-2 space-y-1.5">
                {groups.map((g, gi) => (
                  <div key={gi}>
                    {g.cau && <div className="font-black text-emerald-800">{g.cau}</div>}
                    <ul className="space-y-0.5">
                      {g.idxs.map((i) => (
                        <li key={i} className="flex justify-between gap-2 pl-1">
                          <span>{rubric[i].label}</span><span className="font-bold">{mine!.itemScores![i] ?? 0}/{rubric[i].max}đ</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
            {mine?.feedback && (
              <p className="text-sm text-emerald-900 flex items-start gap-1.5 mt-2">
                <MessageSquare size={15} className="mt-0.5 shrink-0" /> <span className="whitespace-pre-wrap">{mine.feedback}</span>
              </p>
            )}
            {feedbackAudioUrl && (
              <div className="mt-3">
                <div className="text-sm font-semibold text-emerald-800 flex items-center gap-1.5"><Mic size={15} /> Audio phản hồi</div>
                <audio controls src={feedbackAudioUrl} className="mt-2 w-full max-w-xl" />
              </div>
            )}
            {feedbackVideoUrl && (
              <div className="mt-3">
                <div className="text-sm font-semibold text-emerald-800 flex items-center gap-1.5"><Video size={15} /> Video phản hồi</div>
                {isDirectVideo(feedbackVideoUrl) ? (
                  <video controls src={feedbackVideoUrl} className="mt-2 w-full max-w-2xl rounded-xl border border-emerald-200 bg-black/5" />
                ) : (
                  <a href={feedbackVideoUrl} onClick={openExternalClick(feedbackVideoUrl)} className="mt-2 inline-flex items-center gap-1.5 text-sm font-bold text-emerald-700 hover:text-emerald-900">
                    <ExternalLink size={14} /> Mở video phản hồi
                  </a>
                )}
              </div>
            )}
          </div>
        )}

        {/* Giáo viên ghi chú/chấm bút trực tiếp lên bài của HS */}
        {annos.length > 0 && (
          <div className="bg-white rounded-2xl border border-amber-200 shadow-sm p-5 mb-4">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2 text-amber-800 font-black"><Pencil size={16} /> Giáo viên ghi chú trên bài của bạn</div>
              <button type="button"
                onClick={() => annos.forEach((u, i) => downloadFile(u, `bai-cham-${i + 1}.${(u.split('?')[0].split('.').pop() || 'png')}`))}
                className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-bold text-amber-800 hover:bg-amber-100">
                <Download size={15} /> Tải bài chấm
              </button>
            </div>
            <div className="space-y-3">
              {annos.map((u, i) => (
                <AnnotatedImage key={i} url={u} alt={`Bài đã chấm ${i + 1}`} thumbClass="w-full rounded-xl border border-amber-200" downloadName={`bai-cham-${i + 1}.png`} />
              ))}
            </div>
          </div>
        )}

        {/* Khu nộp bài */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <h2 className="font-bold text-slate-800 mb-3">{graded ? 'Bài làm của bạn' : mine ? 'Bài đã nộp (có thể sửa & nộp lại)' : 'Nộp bài làm'}</h2>

          {/* File lời giải (ảnh/PDF) */}
          <div className="space-y-2 mb-3">
            {files.map((u, i) => (
              <div key={i} className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 bg-gray-50">
                {isPdf(u) ? <FileText size={16} className="text-red-500 shrink-0" /> : <ImageIcon size={16} className="text-blue-500 shrink-0" />}
                <a href={u} onClick={openExternalClick(u)} className="flex-1 min-w-0 truncate text-sm text-blue-600 hover:underline">{u.split('/').pop()}</a>
                {!graded && <button onClick={() => setFiles((p) => p.filter((_, k) => k !== i))} className="p-1 text-rose-400 hover:text-rose-600"><X size={14} /></button>}
              </div>
            ))}
            {!graded && (
              <label className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-sky-100 text-sky-700 text-sm font-bold hover:bg-sky-200 cursor-pointer">
                {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Tải lên ảnh/PDF lời giải
                <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onPickFile} disabled={uploading} />
              </label>
            )}
          </div>

          <textarea
            value={content} onChange={(e) => setContent(e.target.value)} rows={6} disabled={graded}
            placeholder="Hoặc gõ bài làm / ghi chú thêm ở đây (tuỳ chọn)…"
            className="w-full p-3 border border-gray-200 rounded-xl text-sm leading-relaxed outline-none focus:ring-2 focus:ring-blue-200 resize-y disabled:bg-gray-50"
          />
          {msg && <p className="text-sm text-slate-600 mt-3">{msg}</p>}
          {!graded && (
            <button onClick={submit} disabled={busy || uploading}
              className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 disabled:opacity-50">
              {busy ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              {mine ? 'Nộp lại' : 'Nộp bài'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default AssignmentDoPage
